import Loader from '@components/Loader'
import { useConcierge } from '@hooks/useConcierge'
import {
  asBillingAccount,
  BillingAccount,
  SubscriptionContext,
} from '@hooks/useSubscription'
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore'
import { ReactNode, useEffect, useState } from 'react'

interface SubscriptionProviderProps {
  children: ReactNode
}

const BILLING_ACCOUNTS_COLLECTION = 'billing_accounts'

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const [billingAccount, setBillingAccount] = useState<BillingAccount>()
  const [loading, setLoading] = useState(true)
  const { concierge } = useConcierge()
  const db = getFirestore()

  useEffect(() => {
    let unsubscribe: () => void

    async function fetchSubscription() {
      const q = query(
        collection(db, BILLING_ACCOUNTS_COLLECTION),
        where('email', '==', concierge.email),
      )
      const snapshot = await getDocs(q)

      if (snapshot.empty) {
        console.error(`No subscription found for ${concierge.email}`)
        setBillingAccount({
          email: concierge.email,
          subscription: {
            product: 'missing',
            status: 'Error! Contact customer support.',
          },
        } as BillingAccount)
        setLoading(false)
        return
      }

      const firstDoc = snapshot.docs[0]
      const docsFound = snapshot.docs.length

      console.log('Subscriptions found', docsFound)
      const id = firstDoc.id
      const docRef = doc(db, BILLING_ACCOUNTS_COLLECTION, id)
      unsubscribe = onSnapshot(docRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const ba = asBillingAccount(docSnapshot.data() as BillingAccount)
          setBillingAccount(ba)
        } else {
          console.error('BA document no longer exists')
        }
        setLoading(false)
      })
    }

    fetchSubscription()

    return function cleanup() {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [concierge.email, db])

  if (billingAccount === undefined || loading) {
    return <Loader />
  }

  return (
    <SubscriptionContext.Provider
      value={{
        billingAccount: billingAccount,
        setBillingAccount: setBillingAccount,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  )
}
